import {isPossiblePhoneNumber} from 'libphonenumber-js';
import React, {FC, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';

import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {
  buildCancelTokenSource,
  CancelTokenSource,
} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {IncomingCallContact} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {ApplicationInboxSidePanelEntryPointModel} from '../../../models/api/applicationEntryPointModel';
import {AppState} from '../../../store/appState';
import {fetchAccountAsyncAction} from '../../../store/data/accounts/accountsActions';
import {computeApplicationIdFromVoiceBannerId} from '../../../store/data/applications/applicationsNotificationsActions';
import {getInboxSidePanelEntryPoints} from '../../../store/data/applications/applicationsSelectors';
import {fetchCardsByQueryAsyncAction} from '../../../store/data/cards/cardsActions';
import {VoiceBannerState} from '../../../store/panels/app/banners/bannersState';
import {showApplicationIncomingCallNotificationAction} from '../../../store/stateless/notificationsActions';
import {ApplicationIncomingCallBanner} from './applicationIncomingCallBanner';
import {formatPhoneNumber} from './phoneHelpers';

/*
 * Constants.
 */

const queryMatchCategory = 'phone';
const anonymous = 'Anonymous';

/*
 * Props.
 */

interface ApplicationIncomingCallContainerProps {
  bannerState: VoiceBannerState;
  panelId: AppPanelId;
}

interface ApplicationIncomingCallBannerContainerComponentProps {
  bannerState: VoiceBannerState;
  panelId: AppPanelId;
  applicationId: number;
  entryPoint?: ApplicationInboxSidePanelEntryPointModel;
}

interface InternalCardContact {
  contactName?: string;
  accountName?: string;
}

/*
 * Selectors.
 */

function getApplicationId(_state: AppState, props: ApplicationIncomingCallContainerProps) {
  return computeApplicationIdFromVoiceBannerId(props.bannerState.uid);
}

const getEntryPoint = createSelector(
  [getInboxSidePanelEntryPoints, getApplicationId],
  (entryPoints, applicationId) => entryPoints.find((ep) => ep.applicationId === applicationId),
);

/*
 * Container component.
 */

const ApplicationIncomingCallBannerContainerComponent: FC<
  ApplicationIncomingCallBannerContainerComponentProps
> = (props) => {
  const {panelId, bannerState, entryPoint, applicationId} = props;
  const {incomingCallContact} = bannerState;
  const dispatch = useAppDispatch();

  const [internalContact, setInternalContact] = useState<InternalCardContact | undefined>();
  const [isFetchComplete, setIsFetchComplete] = useState<boolean>(false);
  const isFetchCompleteRef = useRef(false);
  const [cardCancelTokenSource, setCardCancelTokenSource] = useState<CancelTokenSource | undefined>(
    undefined,
  );
  const [accountCancelTokenSource, setAccountCancelTokenSource] = useState<CancelTokenSource | undefined>(
    undefined,
  );

  // Fetch the contact card and do not render the banner until fetch is complete.
  useInitialEffect(() => {
    async function fetchContactCard() {
      const query = incomingCallContact?.handle;
      if (!query) {
        setIsFetchComplete(true);
        return;
      }

      try {
        const formattedQuery = formatPhoneNumber(query);

        const newCardCancelTokenSource = buildCancelTokenSource();
        setCardCancelTokenSource(newCardCancelTokenSource);

        const {cards} = await dispatch(
          fetchCardsByQueryAsyncAction({
            query: formattedQuery ?? query,
            limit: 1,
            offset: 0,
            cancelToken: newCardCancelTokenSource.token,
          }),
        );

        if (cards.length < 1) {
          setIsFetchComplete(true);
          return;
        }

        const card = cards[0];

        // Check that the card has a contact that is a phone number match.
        if (!card.contactUids.find((uid) => uid.includes(queryMatchCategory))) {
          setIsFetchComplete(true);
          return;
        }

        // Maybe fetch the account associated with the contact card.
        if (card.accountId) {
          const newAccountCancelTokenSource = buildCancelTokenSource();
          setAccountCancelTokenSource(newAccountCancelTokenSource);

          const account = await dispatch(
            fetchAccountAsyncAction(card.accountId, {cancelToken: newAccountCancelTokenSource.token}),
          );
          setInternalContact({contactName: card.name, accountName: account.name});
        } else {
          setInternalContact({contactName: card.name});
        }

        setIsFetchComplete(true);
      } catch (err) {
        setIsFetchComplete(true);
        reportToBugsnag(new Error(`Failed to fetch contact card for incoming call banner: ${query}`));
      }
      setCardCancelTokenSource(undefined);
    }
    fetchContactCard();

    return () => {
      // Clean up by cancelling the fetch.
      if (cardCancelTokenSource) {
        cardCancelTokenSource.cancel();
      }
      if (accountCancelTokenSource) {
        accountCancelTokenSource.cancel();
      }
    };
  });

  useEffect(() => {
    if (isFetchCompleteRef.current || !isFetchComplete) {
      return;
    }

    isFetchCompleteRef.current = true;

    // Show system notification just once after fetch is completed.
    dispatch(
      showApplicationIncomingCallNotificationAction(
        bannerState.uid,
        computeNameToDisplay(incomingCallContact, internalContact),
        incomingCallContact?.handle,
      ),
    );
  }, [applicationId, incomingCallContact, dispatch, internalContact, isFetchComplete, bannerState.uid]);

  if (!entryPoint) {
    return null;
  }

  if (isFetchComplete) {
    return (
      <ApplicationIncomingCallBanner
        panelId={panelId}
        bannerState={bannerState}
        entryPoint={entryPoint}
        contactName={computeNameToDisplay(incomingCallContact, internalContact)}
      />
    );
  }

  return null;
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ApplicationIncomingCallContainerProps>()(() => ({
  entryPoint: getEntryPoint,
  applicationId: getApplicationId,
}));

export const ApplicationIncomingCallBannerContainer = connect(mapStateToProps)(
  ApplicationIncomingCallBannerContainerComponent,
);

/*
 * Helper functions.
 */

// Determine which name we will show in the notifications.
function computeNameToDisplay(
  externalContact?: IncomingCallContact,
  internalContact?: InternalCardContact,
): string | undefined {
  if (internalContact) {
    const {contactName, accountName} = internalContact;
    // If the internal contact name is undefined or a number, name to display is undefined.
    if (!contactName || isPossiblePhoneNumber(formatPhoneNumber(contactName) ?? '')) {
      return undefined;
    }
    // Append the company name in parentheses if provided.
    return accountName ? `${contactName} (${accountName})` : contactName;
  }

  if (externalContact) {
    const {name} = externalContact;
    // If the external contact name is a number, name to display is undefined.
    return isPossiblePhoneNumber(formatPhoneNumber(name) ?? '') ? undefined : name;
  }

  // If we have no contact information at all, show Anonymous.
  return anonymous;
}
